/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { Link, useLocation } from "react-router-dom";
import { LI, SVG, UL } from "../../AbstractElements";
import { Href } from "../../utils/Constant";
import { MenuListType, SidebarItemTypes, MenuItem } from "../../Types/Layout/Sidebar";
import { handlePined } from "../../ReduxToolkit/Reducer/LayoutSlice";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import FeatherIcons from "../../CommonElements/FeatherIcon/FeatherIcons";

// Iconos disponibles en el sprite SVG
const spriteIcons = ['home', 'widget', 'layout', 'project', 'file', 'board', 'ecommerce', 'email', 'chat', 'user', 'bookmark', 'contact', 'task', 'calendar', 'social', 'to-do', 'search', 'form', 'table', 'ui-kits', 'bonus-kit', 'builders', 'animation', 'icons', 'button', 'charts', 'landing-page', 'sample-page', 'internationalization', 'starter-kit', 'others', 'gallery', 'blog', 'faq', 'job-search', 'learning', 'maps', 'editors', 'knowledgebase', 'support-tickets'];

const MenuLists: React.FC<MenuListType> = ({ menu, setActiveMenu, activeMenu, level, className }) => {
  const { pinedMenu } = useAppSelector((state) => state.layout);
  const { sidebarIconType } = useAppSelector((state) => state.themeCustomizer);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { hasRole, user } = useAuth();

  // Función para renderizar el icono (sprite SVG o FeatherIcons)
  const renderIcon = (iconName: string | undefined) => {
    if (!iconName) return null;
    
    // Si el icono existe en el sprite, usar SVG
    if (spriteIcons.includes(iconName)) {
      return (
        <span style={{ 
          display: 'inline-block',
          width: '18px',
          height: '18px',
          marginRight: '10px',
          verticalAlign: 'middle'
        }}>
          <SVG 
            className={`${sidebarIconType}-icon`} 
            iconId={`${sidebarIconType}-${iconName}`}
          />
        </span>
      );
    }
    
    // Si no existe en el sprite, usar FeatherIcons
    // Mapear nombres de iconos a nombres de FeatherIcons (solo iconos que existen en react-feather)
    const iconMap: { [key: string]: keyof typeof import("react-feather") } = {
      'file-text': 'FileText',
      'briefcase': 'Briefcase',
      'tool': 'Settings', // Tool no existe, usar Settings
      'book': 'BookOpen', // BookOpen existe
      'file-check': 'CheckCircle', // FileCheck no existe, usar CheckCircle
      'users': 'Users',
      'rss': 'Radio', // Rss no existe, usar Radio como alternativa para noticias
      'shield': 'Shield',
      'globe': 'Map', // Globe puede no verse bien, usar Map
      'map': 'Map', // Map existe en FeatherIcons
      'dollar-sign': 'DollarSign',
      'bell': 'Bell',
      'bar-chart': 'BarChart2', // BarChart2 existe
      'settings': 'Settings',
      'clock': 'Clock',
      'clipboard': 'Clipboard', // Para seguimiento de casos
      'search': 'Search', // Para búsqueda
    };
    
    // Obtener el icono del mapa o usar un fallback
    let featherIconName: keyof typeof import("react-feather") = iconMap[iconName] || 'Circle';
    
    // Verificar que el icono existe en react-feather antes de usarlo
    const Icon = require("react-feather");
    if (!Icon[featherIconName]) {
      // Si el icono no existe, usar un icono por defecto
      featherIconName = 'Circle';
    }
    
    return (
      <span style={{ 
        display: 'inline-block',
        width: '18px',
        height: '18px',
        marginRight: '10px',
        verticalAlign: 'middle'
      }}>
        <FeatherIcons 
          iconName={featherIconName}
          className={`${sidebarIconType}-icon`}
        />
      </span>
    );
  };
  const ActiveNavLinkUrl = (path?: string) => {
    return location.pathname === path ? true : "";
  };
  const shouldSetActive = ({ item }: SidebarItemTypes) => {
    var returnValue = false;
    if (item?.path === location.pathname) returnValue = true;
    if (!returnValue && item?.children) {
      item?.children.every((subItem) => {
        returnValue = shouldSetActive({ item: subItem });
        return !returnValue;
      });
    }
    return returnValue;
  };
  const handleClick = (item: string) => {
    const temp = activeMenu;
    temp[level] = item !== temp[level] ? item : "";
    setActiveMenu([...temp]);
  };
  useEffect(() => {
    menu?.forEach((item: any) => {
      let gotValue = shouldSetActive({ item });
      if (gotValue) {
        let temp = [...activeMenu];
        temp[level] = item.title;
        setActiveMenu(temp);
      }
    });
  }, []);

  // Filtrar elementos del menú basado en roles requeridos
  const filterMenuByRole = (items: any[]): any[] => {
    if (!items || items.length === 0) return [];
    
    return items
      .map((item) => {
        // Si tiene children, filtrar primero los hijos
        let filteredItem = { ...item };
        if (item.children && item.children.length > 0) {
          filteredItem.children = filterMenuByRole(item.children);
        }
        return filteredItem;
      })
      .filter((item) => {
        // Si tiene requiredRole, verificar que el usuario tenga ese rol
        if (item.requiredRole) {
          // Debug: verificar rol del usuario
          if (process.env.NODE_ENV === 'development' && item.title === 'Gestión de Usuarios') {
            console.log('Verificando rol para Gestión de Usuarios:', {
              requiredRole: item.requiredRole,
              user: user,
              userRole: user?.role?.name,
              userRoleObject: user?.role,
              hasRole: hasRole(item.requiredRole),
              localStorageUser: localStorage.getItem('user')
            });
          }
          const hasRequiredRole = hasRole(item.requiredRole);
          // Si no tiene el rol requerido, ocultar el item
          if (!hasRequiredRole) {
            return false;
          }
        }
        // Si tiene children, verificar si quedan hijos después del filtrado
        if (item.children && item.children.length > 0) {
          // Si quedan hijos, mostrar el item
          return true;
        } else if (item.children && item.children.length === 0) {
          // Si no quedan hijos pero el item tenía children, ocultarlo
          // (a menos que el item mismo tenga un path)
          return !!item.path;
        }
        // Si no tiene requiredRole, mostrar el item
        return true;
      });
  };

  const filteredMenu = filterMenuByRole(menu || []);

  return (
    <>
      {filteredMenu?.map((item, index) => (
        <LI
          key={index}
          className={`${level === 0 ? "sidebar-list" : ""} ${pinedMenu.includes(item.title || "") ? "pined" : ""}  
          ${(item.children ? item.children.map((innerItem: MenuItem) => ActiveNavLinkUrl(innerItem.path)).includes(true) : ActiveNavLinkUrl(item.path)) || activeMenu[level] === item.title ? "active" : ""} `}
        >
          {level === 0 && <i className="fa fa-thumb-tack" onClick={() => dispatch(handlePined(item.title))}></i>}
          <Link
            className={`${!className && level !== 2 ? "sidebar-link sidebar-title" : ""} 
            ${(item.children ? item.children.map((innerItem: MenuItem) => ActiveNavLinkUrl(innerItem.path)).includes(true) : ActiveNavLinkUrl(item.path)) || activeMenu[level] === item.title ? "active" : ""}
            ${item.path === "#" ? "disabled-link" : ""}`}
            to={item.path ? item.path : Href}
            onClick={(e) => {
              if (item.children && !item.path) {
                e.preventDefault();
              }
              if (item.path === "#") {
                e.preventDefault();
                return;
              }
              handleClick(item.title);
            }}
            style={item.path === "#" ? { cursor: "not-allowed", opacity: 0.6 } : {}}
          >
            {renderIcon(item.icon)}
            <span className={item.lanClass && item.lanClass}>{t(item.title)}</span>
            {item.children &&
              (activeMenu[level] === item.title ? (
                <div className="according-menu">
                  <i className="fa fa-angle-down" />
                </div>
              ) : (
                <div className="according-menu">
                  <i className="fa fa-angle-right" />
                </div>
              ))}
          </Link>
          {item.children && (
            <UL
              className={`simple-list ${level !== 0 ? "nav-sub-childmenu submenu-content" : "sidebar-submenu "}`}
              style={{
                display: `${(item.children ? item.children.map((innerItem: MenuItem) => ActiveNavLinkUrl(innerItem.path)).includes(true) : ActiveNavLinkUrl(item.path)) || activeMenu[level] === item.title ? "block" : "none"}`,
              }}
            >
              <MenuLists menu={item.children} activeMenu={activeMenu} setActiveMenu={setActiveMenu} level={level + 1} className="sidebar-submenu" />
            </UL>
          )}
        </LI>
      ))}
    </>
  );
};

export default MenuLists;
